import React, { useEffect, useRef, useState } from 'react';
import { Spin } from 'antd';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import * as API from '../services/api';
import { colors } from '../theme';

export const wikiMarkdownPreviewShellStyle = (isLight) => ({
  background: isLight ? '#ffffff' : (colors.bgElevated || '#1A1635'),
  border: `1px solid ${isLight ? 'rgba(15, 23, 42, 0.12)' : (colors.border || 'rgba(255,255,255,0.08)')}`,
  borderRadius: 8,
  padding: '20px 28px',
  maxHeight: '70vh',
  overflow: 'auto',
  overflowAnchor: 'none',
  fontFamily: "Segoe UI, -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif",
  fontSize: 14,
  lineHeight: 1.65,
  color: isLight ? '#24292f' : (colors.textPrimary || '#F0EDF5'),
});

export const wikiRawPreviewShellStyle = (isLight) => ({
  background: isLight ? '#f8fafc' : (colors.bgElevated || '#1A1635'),
  border: `1px solid ${isLight ? 'rgba(15, 23, 42, 0.12)' : (colors.border || 'rgba(255,255,255,0.08)')}`,
  borderRadius: 8,
  padding: '20px 24px',
  maxHeight: '70vh',
  overflow: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
  fontSize: 13,
  lineHeight: 1.7,
  color: isLight ? '#0f172a' : (colors.textPrimary || '#F0EDF5'),
});

const AZURE_WIKI_IMG_SCHEME = 'azure-wiki-img:';

export const parseAzureWikiImageSrc = (src = '') => {
  const raw = String(src || '').trim();
  if (!raw) return null;
  let path = '';
  if (raw.toLowerCase().startsWith(AZURE_WIKI_IMG_SCHEME)) {
    path = raw.slice(AZURE_WIKI_IMG_SCHEME.length).trim();
  } else if (raw.startsWith('/.attachments/')) {
    path = raw.split('?')[0].split('#')[0];
  } else {
    return null;
  }
  try {
    path = decodeURIComponent(path);
  } catch (_e) {
    /* keep raw path if malformed encoding */
  }
  return path.split('?')[0].split('#')[0];
};

/** Keep azure-wiki-img: (and /.attachments/) — defaultUrlTransform strips unknown schemes. */
export const azureWikiMarkdownUrlTransform = (url) => {
  const value = String(url || '');
  const lower = value.toLowerCase();
  if (lower.startsWith(AZURE_WIKI_IMG_SCHEME) || value.startsWith('/.attachments/')) {
    return value;
  }
  return defaultUrlTransform(value);
};

/** Authenticated Azure Wiki image for the preview modal (Bearer → blob URL). */
export function AzureWikiAuthImage({
  src,
  alt,
  token,
  projectId,
  fileKey,
  docId,
  registerBlobUrl,
}) {
  const [blobUrl, setBlobUrl] = useState('');
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const wrapRef = useRef(null);

  const FRAME_STYLE = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: 'min(420px, 50vh)',
    minHeight: 280,
    margin: '12px 0 8px',
    overflow: 'auto',
    overflowAnchor: 'none',
    scrollMargin: 0,
    background: 'rgba(127,127,127,0.06)',
    borderRadius: 4,
  };

  useEffect(() => {
    let cancelled = false;
    let createdUrl = '';
    const attachmentPath = parseAzureWikiImageSrc(src);
    if (!attachmentPath || !token || !projectId) {
      setLoading(false);
      setFailed(true);
      return undefined;
    }
    setLoading(true);
    setFailed(false);
    (async () => {
      try {
        const resp = await API.getAzureWikiImage(token, projectId, {
          file_key: fileKey || undefined,
          doc_id: docId || undefined,
          path: attachmentPath,
        });
        const blob = resp?.data instanceof Blob
          ? resp.data
          : new Blob([resp?.data]);
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        if (typeof registerBlobUrl === 'function') {
          registerBlobUrl(createdUrl);
        }
        setBlobUrl(createdUrl);
      } catch (_e) {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [src, token, projectId, fileKey, docId, registerBlobUrl]);

  return (
    <div
      ref={wrapRef}
      style={FRAME_STYLE}
      onMouseDown={(e) => {
        e.preventDefault();
      }}
    >
      {loading ? <Spin size="small" /> : null}
      {!loading && (failed || !blobUrl) ? (
        <div style={{ color: '#8c8c8c', fontSize: 12, padding: 8 }}>
          {alt ? `[Image: ${alt}]` : '[Image unavailable]'}
        </div>
      ) : null}
      {!loading && blobUrl ? (
        <img
          src={blobUrl}
          alt={alt || ''}
          draggable={false}
          tabIndex={-1}
          style={{
            display: 'block',
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            borderRadius: 4,
            overflowAnchor: 'none',
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Rendered markdown body matching the RAG Pipeline file preview style.
 */
export function WikiMarkdownPreviewBody({
  content,
  isLight = true,
  token,
  projectId,
  fileKey = '',
  docId = '',
  sourceKind = '',
  registerBlobUrl,
}) {
  return (
    <div style={wikiMarkdownPreviewShellStyle(isLight)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        urlTransform={azureWikiMarkdownUrlTransform}
        components={{
          h1: ({ children }) => (
            <h1 style={{
              fontSize: 26,
              margin: '0 0 14px',
              color: isLight ? '#0b5cab' : '#7eb6ff',
              borderBottom: isLight ? '1px solid #d0d7de' : '1px solid rgba(255,255,255,0.12)',
              paddingBottom: 8,
            }}
            >
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 style={{
              fontSize: 20,
              margin: '22px 0 10px',
              color: isLight ? '#163a5f' : '#c9d7f0',
              borderBottom: isLight ? '1px solid #eaeef2' : '1px solid rgba(255,255,255,0.08)',
              paddingBottom: 6,
            }}
            >
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 style={{ fontSize: 16, margin: '18px 0 8px', fontWeight: 600 }}>{children}</h3>
          ),
          p: ({ children }) => (
            <p style={{ margin: '0 0 10px' }}>{children}</p>
          ),
          ul: ({ children }) => (
            <ul style={{ margin: '0 0 10px 22px', padding: 0 }}>{children}</ul>
          ),
          ol: ({ children }) => (
            <ol style={{ margin: '0 0 10px 22px', padding: 0 }}>{children}</ol>
          ),
          li: ({ children }) => (
            <li style={{ marginBottom: 4 }}>{children}</li>
          ),
          table: ({ children }) => (
            <div style={{ overflowX: 'auto', margin: '10px 0 14px' }}>
              <table style={{
                borderCollapse: 'collapse',
                width: '100%',
                fontSize: 13,
              }}
              >
                {children}
              </table>
            </div>
          ),
          th: ({ children }) => (
            <th style={{
              border: isLight ? '1px solid #d0d7de' : '1px solid rgba(255,255,255,0.15)',
              background: isLight ? '#f6f8fa' : 'rgba(255,255,255,0.06)',
              padding: '6px 10px',
              textAlign: 'left',
            }}
            >
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td style={{
              border: isLight ? '1px solid #d0d7de' : '1px solid rgba(255,255,255,0.12)',
              padding: '6px 10px',
              verticalAlign: 'top',
            }}
            >
              {children}
            </td>
          ),
          code: ({ inline, children }) => (
            inline ? (
              <code style={{
                fontFamily: 'Consolas, monospace',
                fontSize: 12.5,
                background: isLight ? '#f6f8fa' : 'rgba(255,255,255,0.08)',
                padding: '1px 5px',
                borderRadius: 4,
              }}
              >
                {children}
              </code>
            ) : (
              <code style={{ display: 'block', whiteSpace: 'pre-wrap' }}>{children}</code>
            )
          ),
          pre: ({ children }) => (
            <pre style={{
              background: isLight ? '#f6f8fa' : 'rgba(0,0,0,0.35)',
              border: isLight ? '1px solid #d0d7de' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: 6,
              padding: 12,
              overflowX: 'auto',
              fontSize: 12.5,
              lineHeight: 1.5,
            }}
            >
              {children}
            </pre>
          ),
          blockquote: ({ children }) => (
            <blockquote style={{
              margin: '10px 0',
              padding: '6px 12px',
              borderLeft: '3px solid #0b5cab',
              background: isLight ? '#f6f8fa' : 'rgba(11,92,171,0.12)',
              color: isLight ? '#57606a' : '#c8d0dc',
            }}
            >
              {children}
            </blockquote>
          ),
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noreferrer" style={{ color: '#0b5cab' }}>
              {children}
            </a>
          ),
          img: ({ src, alt }) => {
            const wikiPath = parseAzureWikiImageSrc(src);
            if (wikiPath && sourceKind === 'azure_wiki') {
              return (
                <AzureWikiAuthImage
                  src={src}
                  alt={alt}
                  token={token}
                  projectId={projectId}
                  fileKey={fileKey}
                  docId={docId}
                  registerBlobUrl={registerBlobUrl}
                />
              );
            }
            if (!src) return null;
            return (
              <img
                src={src}
                alt={alt || ''}
                style={{
                  display: 'block',
                  maxWidth: '100%',
                  height: 'auto',
                  margin: '12px 0 8px',
                  borderRadius: 4,
                }}
              />
            );
          },
          hr: () => (
            <hr style={{
              border: 'none',
              borderTop: isLight ? '1px solid #d0d7de' : '1px solid rgba(255,255,255,0.12)',
              margin: '16px 0',
            }}
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
